import { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import {
  PAVILION_SHELL_BORDER,
  PAVILION_SHELL_GRADIENT,
  PAVILION_SHELL_RADIUS,
  PAVILION_WORK_AREA_MARGIN_PX,
} from './pavilionShellStyles';

/** Guest / minimal pavilion container — must match Electron min size for steam install. */
export const GUEST_APP_WIDTH = 350;
export const GUEST_APP_HEIGHT = 530;

export const PRE_MINIMAL_LOGIN_BOUNDS_KEY = 'playcast_pre_minimal_login_bounds';

export const MinimalSteamLogin = () => {
  const context = useContext(PlaycastContext);
  const [isWindowBlurred, setIsWindowBlurred] = useState(false);

  const versionState = context.stateStores.versionState;
  const hostStatus = context.stateStores.hostState.status;
  const hostAllowsUpdateUi = hostStatus !== 'streaming' && hostStatus !== 'starting';
  const updateIgnored = (() => {
    try {
      const raw = localStorage.getItem('ignoredUpdates');
      const list: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) && list.includes(versionState.updateVersion);
    } catch { return false; }
  })();
  const needsUpdate = Boolean(hostAllowsUpdateUi && versionState.hasUpdate && !updateIgnored);

  useEffect(() => {
    let mounted = true;
    window.electron?.on('pavilion-window-blur', () => {
      if (mounted) setIsWindowBlurred(true);
    });
    window.electron?.on('pavilion-window-focus', () => {
      if (mounted) setIsWindowBlurred(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const wc = window.windowControls;
    if (!wc || !window.playcast?.steamInstall || window.playcast?.guestAccount) {
      console.log('[PlaycastWindowBounds] MinimalSteamLogin skip', {
        hasWindowControls: !!wc,
        steamInstall: window.playcast?.steamInstall,
        guestAccount: window.playcast?.guestAccount,
      });
      return;
    }

    let cancelled = false;
    console.log('[PlaycastWindowBounds] viewport (CSS px)', {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    });
    void (async () => {
      try {
        const b = await wc.getBounds();
        if (cancelled) return;
        const tw = GUEST_APP_WIDTH;
        const th = GUEST_APP_HEIGHT;
        console.log('[PlaycastWindowBounds] MinimalSteamLogin getBounds', b, 'target', { w: tw, h: th });
        // Already compact: main process placed bottom-right; avoid nudging position.
        if (b.width <= tw + 8 && b.height <= th + 8) {
          return;
        }
        // Only remember bounds if we are shrinking from a larger window (skip first-launch compact default).
        if (b.width > tw + 8 || b.height > th + 8) {
          sessionStorage.setItem(PRE_MINIMAL_LOGIN_BOUNDS_KEY, JSON.stringify(b));
        }
        const m = PAVILION_WORK_AREA_MARGIN_PX;
        let nx = Math.round(b.x + b.width - tw - m);
        let ny = Math.round(b.y + b.height - th - m);
        if (nx < b.x) nx = b.x;
        if (ny < b.y) ny = b.y;
        if (nx + tw > b.x + b.width) nx = b.x + b.width - tw;
        if (ny + th > b.y + b.height) ny = b.y + b.height - th;
        const next = { x: nx, y: ny, width: tw, height: th };
        console.log('[PlaycastWindowBounds] MinimalSteamLogin setBounds', next);
        await wc.setBounds(next);
        const after = await wc.getBounds();
        console.log('[PlaycastWindowBounds] MinimalSteamLogin getBounds after setBounds', after);
      } catch (e) {
        console.warn('[MinimalSteamLogin] window resize failed', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = () => {
    context.electronFunctions?.hostLogin();
  };

  const handlePrimaryAction = () => {
    if (needsUpdate) {
      context.targetFunctions.startUpdate();
    } else {
      handleLogin();
    }
  };

  const handleIgnoreUpdate = () => {
    context.targetFunctions.ignoreUpdate();
  };

  const handleClose = () => {
    const electronFunctions = context.electronFunctions;
    if (electronFunctions) {
      if (window.playcast?.guestAccount) {
        Sentry.captureMessage('Guest Mode Host App Closed', {
          level: 'error',
          tags: {
            event_type: 'guest_mode_host_app_closed_from_close_button',
            guest_mode: String(window.playcast?.guestAccount),
            version: constants.VERSION,
          },
        });
      }
      electronFunctions.setWindowState('closed');
    }
  };

  return (
    <div className={`box-border flex h-full min-h-0 w-full flex-1 overflow-hidden ${isWindowBlurred ? PAVILION_SHELL_GRADIENT : 'bg-transparent'}`}>
      <div
        className={`relative box-border flex h-full min-h-0 w-full flex-col overflow-hidden ${PAVILION_SHELL_RADIUS} ${PAVILION_SHELL_BORDER} ${PAVILION_SHELL_GRADIENT} pt-5 pb-5 pl-[7px] pr-[7px]`}
      >
        <button
          type='button'
          onClick={handleClose}
          className='absolute right-2 top-2 z-10 flex size-9 items-center justify-center rounded-md text-[#FBBF24] transition-colors hover:text-[#FCD34D] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]'
          aria-label='Close'
        >
          <FontAwesomeIcon icon={faXmark} className='size-5' />
        </button>

        <div className='flex min-h-0 flex-1 flex-col items-center justify-center'>
          <img src='/playcast-gibber.svg' alt='Playcast' className='w-[139px] h-[112px] shrink-0 object-contain' />

          <h1 className='shrink-0 text-[32px] font-bold tracking-tight text-[#FBBF24] font-["Inter"]'>Playcast</h1>

          <button
            type='button'
            onClick={handlePrimaryAction}
            className='mt-8 shrink-0 rounded-full bg-[#FBBF24] px-12 py-2.5 text-center text-xl font-semibold text-[#020617] transition-transform hover:scale-[1.02] active:scale-[0.98]'
          >
            {needsUpdate ? 'Update' : 'Login'}
          </button>
          {needsUpdate && constants.ENVIRONMENT === 'development' && (
            <button
              type='button'
              onClick={handleIgnoreUpdate}
              className='mt-4 shrink-0 text-center text-xs text-[#FBBF24]/80 underline-offset-2 hover:text-[#FBBF24] hover:underline'
              data-testid='ignore-update-button'
            >
              Ignore (im a dev)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
